/**
 * useNotificacoesRealtime — subscreve em mudancas na tab_notificacoes_in_app
 * para o usuário atual via Supabase Realtime e:
 *   1. Inválida queries 'notificações' e 'notif-unread' (badge atualiza no ato).
 *   2. Em INSERT, dispara um toast leve com o titulo (a menos que silenciado).
 *
 * Hardening: silenciarToast vai pra um ref para não re-subscrever o canal
 * (e perder mensagens em transit) toda vez que muda de rota.
 *
 * Tolerante a falhas: se Realtime não estiver habilitado para a tabela ou a
 * connection cair, não quebra a UI — o refetchInterval do badge (30s) continua
 * funcionando como fallback.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';

interface NotifPayload {
  seq_notificacao: number;
  cod_severidade: 'INFO' | 'SUCESSO' | 'AVISO' | 'ERRO';
  des_titulo: string;
  des_corpo: string | null;
  ind_lida: boolean;
}

interface UseNotificacoesRealtimeOptions {
  /** Se true, não mostra toast em INSERT (para usar dentro da própria tela). */
  silenciarToast?: boolean;
}

const TOAST_VARIANTE: Record<NotifPayload['cod_severidade'], (msg: string, opts?: object) => void> =
  {
    INFO: (m, o) => toast.info(m, o),
    SUCESSO: (m, o) => toast.success(m, o),
    AVISO: (m, o) => toast.warning(m, o),
    ERRO: (m, o) => toast.error(m, o),
  };

export function useNotificacoesRealtime(options: UseNotificacoesRealtimeOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ref evita re-subscrever o canal quando silenciarToast muda (ex: navegacao)
  const silenciarRef = useRef(options.silenciarToast ?? false);
  silenciarRef.current = options.silenciarToast ?? false;

  useEffect(() => {
    if (!user?.id) return;

    const canal = supabase
      .channel(`notif:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tab_notificacoes_in_app',
          filter: `cod_auth_user=eq.${user.id}`,
        },
        (payload) => {
          // Inválida ambas queries — badge e lista
          void queryClient.invalidateQueries({ queryKey: ['notif-unread', user.id] });
          void queryClient.invalidateQueries({ queryKey: ['notificacoes', user.id] });

          // Toast apenas em INSERT (notificação nova) e se não silenciado
          if (payload.eventType === 'INSERT' && !silenciarRef.current) {
            const novo = payload.new as NotifPayload;
            const showToast = TOAST_VARIANTE[novo.cod_severidade] ?? TOAST_VARIANTE.INFO;
            showToast(novo.des_titulo, {
              description: novo.des_corpo ?? undefined,
            });
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(canal);
    };
  }, [user?.id, queryClient]);
}
